/**
 * Come back to exactly where you were after an app update (Gordon 2026-10-31:
 * "When updating … the page loaded at the bottom, make sure it remembers
 * exactly where you was on a page and which page").
 *
 * A reload keeps the URL; the scroll position was the problem: it was put back
 * once, two frames after load, before the page's content had arrived, so the
 * jump was clamped to a short page. restoreScrollTo keeps re-applying the
 * position while the page grows and stops once the layout settles or the
 * person scrolls themselves.
 */

const KEY = 'bb-resume';
const MAX_AGE_MS = 5 * 60 * 1000;

const here = () => location.pathname + location.search;

/** Call right before a reload that should land back on the same spot. */
export function saveResumePoint() {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ url: here(), y: Math.round(window.scrollY), at: Date.now() }));
    // The browser's own restore would jump early (and clamp) — the app does it.
    history.scrollRestoration = 'manual';
  } catch { /* storage blocked — a plain reload */ }
}

/** The saved position for this page after an update reload (once), else null. */
export function takeResumePoint(): number | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    sessionStorage.removeItem(KEY);
    const p = JSON.parse(raw) as { url?: string; y?: number; at?: number };
    if (p.url !== here() || typeof p.y !== 'number' || !p.at || Date.now() - p.at > MAX_AGE_MS) return null;
    return p.y;
  } catch { return null; }
}

/**
 * Scroll to `y` as soon as the page is tall enough, and keep it there while
 * content above loads in (up to 10 s, until the height has been stable for
 * 1.5 s). Any wheel / touch / key / mouse press hands control back at once.
 * Returns a cancel function.
 */
export function restoreScrollTo(y: number): () => void {
  let stopped = false;
  const started = Date.now();
  let lastHeight = -1;
  let stableSince = Date.now();
  const stop = () => {
    stopped = true;
    for (const ev of ['wheel', 'touchstart', 'keydown', 'mousedown'] as const) window.removeEventListener(ev, stop);
  };
  for (const ev of ['wheel', 'touchstart', 'keydown', 'mousedown'] as const) window.addEventListener(ev, stop, { passive: true });
  const step = () => {
    if (stopped) return;
    const height = document.documentElement.scrollHeight;
    if (height !== lastHeight) { lastHeight = height; stableSince = Date.now(); }
    if (height - window.innerHeight >= y - 1 && Math.abs(window.scrollY - y) > 1) {
      window.scrollTo({ top: y, left: 0, behavior: 'instant' as ScrollBehavior });
    }
    const reached = Math.abs(window.scrollY - y) <= 1;
    if ((reached && Date.now() - stableSince > 1500) || Date.now() - started > 10_000) return stop();
    setTimeout(step, 100);
  };
  requestAnimationFrame(step);
  return stop;
}
